import { Injectable } from "@angular/core";

@Injectable({
  providedIn: "root",
})
export class TimeUtil {
  private static readonly ISO_DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

  getLastDateRangeFromDays(days: number): {
    startDate: string;
    endDate: string;
  } {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(endDate.getDate() - days);

    return {
      startDate: this.toApiDate(startDate),
      endDate: this.toApiDate(endDate),
    };
  }

  getTodayDateRange(): {
    startDate: string;
    endDate: string;
  } {
    const today = new Date();

    return {
      startDate: this.toApiDate(today),
      endDate: this.toApiDate(today),
    };
  }

  getLastMonthsDateRange(months: number): {
    startDate: string;
    endDate: string;
  } {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setMonth(endDate.getMonth() - months);

    return {
      startDate: this.toApiDate(startDate),
      endDate: this.toApiDate(endDate),
    };
  }

  /**
   * Formats a value as yyyy-MM-dd for API query params.
   * Strings are returned unchanged; Date values use the local calendar day
   * (Material datepicker semantics).
   */
  toApiDate(value: Date | string | null | undefined): string {
    if (value == null || value === "") {
      return "";
    }

    if (typeof value === "string") {
      return value;
    }

    return this.formatLocalCalendarDate(value);
  }

  /**
   * Parses API date-only strings into a local Date for Material datepickers.
   * Example: "2026-03-01" -> local midnight on March 1 (not UTC midnight).
   */
  fromApiDate(value: string | null | undefined): Date | null {
    if (!value) {
      return null;
    }

    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match) {
      const parsed = new Date(value);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }

    const [, year, month, day] = match;
    return new Date(Number(year), Number(month) - 1, Number(day));
  }

  /**
   * Normalizes API date-only strings so UTC display pipes keep the same calendar day.
   * Example: "2026-06-07" -> "2026-06-07T00:00:00Z"
   */
  normalizeApiDateForDisplay(value: string | null | undefined): string {
    if (!value) {
      return "";
    }

    if (TimeUtil.ISO_DATE_ONLY.test(value)) {
      return `${value}T00:00:00Z`;
    }

    return value;
  }

  private formatLocalCalendarDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }
}
